import React, { useContext } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { PDFViewer } from "@react-pdf/renderer";
import { AuthContext } from "../../../../../../auth-context";
import AvatarLogo from "../../../../../../images/theme/avatar/logo-horizontal-dark.png";
import FBBCLogo from "../../../../../../images/theme/fbbc/fbbc-logo-dark.png";
import iconCheck from "../../../../../../images/iconCheck.png";
import Modal from "../../../../../../components/modal-fullscreen";
import styled from "styled-components";
import moment from "moment";
import { ThemeContext } from "styled-components";

// Create styles
const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  image: {
    width: 140,
  },
  sectionHeader: {
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontSize: 10,
    padding: 4,
    marginBottom: 8,
    fontWeight: 700,
    width: "50%",
    // fontFamily: "Oswald",
  },
  metadata: {
    fontSize: 10,
    padding: 4,
    marginBottom: 4,
    fontWeight: 700,
  },
  text: {
    color: "#444",
    fontSize: 12,
    marginTop: 12,
    // fontFamily: "Times-Roman",
  },
  spacer: {
    height: 16,
  },
  option: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 20,
    gap: 2,
  },
  circle: {
    border: "1px solid #555",
    width: 12,
    height: 12,
    borderRadius: "50%",
  },
  circleChecked: {
    width: 12,
    height: 12,
  },
  questionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
  },
  questionDescription: {
    fontSize: 10,
    color: "#555",
    marginBottom: 6,
  },
  optionText: {
    fontSize: 12,
    color: "#555",
  },
  questionContainer: {
    marginBottom: 20,
  },
  responseText: {
    fontSize: 12,
  },
  noResponse: {
    fontSize: 12,
    fontStyle: "italic",
  },
  checkInContainer: {
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
    width: "100%",
  },
  checkInTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
    color: "#333",
  },
  checkInDetail: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
    width: "50%"
  },
  checkInLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: "#555",
  },
  checkInValue: {
    fontSize: 10,
    fontWeight: 600,
    color: "#333",
  },
  photoNote: {
    fontSize: 10,
    color: "#555",
    textAlign: "center",
    marginTop: 8,
    fontStyle: "italic",
  },
  photosContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 8,
    marginBottom: 16,
    gap: 8,
  },
  photoItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  photoImage: {
    width: 170,
    height: 220,
    objectFit: "cover",
  },
  photoLabel: {
    fontSize: 8,
    color: "#555",
    textAlign: "center",
    fontWeight: 500,
    textTransform: "capitalize",
  },
});

Font.register({
  family: "Oswald",
  src: "https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf",
});

const mapLogos = {
  AVATAR: AvatarLogo,
  FBBC: FBBCLogo,
};

// Helper functions
const kgToLbs = (kg) => {
  return (kg * 2.204622).toFixed(1);
};

const calculateLeanBodyMass = (weight, bodyFat) => {
  const leanMass = weight * (1 - bodyFat / 100);
  return leanMass.toFixed(1);
};

const formatDate = (date) => {
  return moment.utc(date).format("MMM D, YYYY");
};

// Create Document Component
const SurveyPDF = ({ data = {}, name, theme, checkInData = {} }) => {
  const { survey_data, response_data } = data;

  const renderResponse = (question, response) => {
    switch (question.responseType) {
      case "text":
        return <Text style={styles.responseText}>{response.response}</Text>;
      case "multiple_choice":
      case "checkbox":
        const options = question?.options?.map((option) => {
          const isChecked = response?.response.includes(option.id);
          return (
            <View key={option.id} style={styles.option}>
              {isChecked ? (
                <Image
                  src={iconCheck}
                  alt="check"
                  style={styles.circleChecked}
                />
              ) : (
                <View style={styles.circle} />
              )}
              <Text style={styles.optionText}>{option.value}</Text>
            </View>
          );
        });
        return options;
      case "linear_scale":
        return <Text style={styles.responseText}>{response.response}</Text>;
      case "select_check_in":
        const checkIn = checkInData[question.id];
        if (checkIn) {
          return (
            <View style={styles.checkInContainer}>
              {checkIn.url_list &&
                checkIn.url_list.length > 0 &&
                checkIn?.trainer_permission_photos !== 0 && (
                  <View style={styles.photosContainer}>
                    {checkIn.url_list.map((urls, index) => {
                      const photoLabel =
                        checkIn.photo_list && checkIn.photo_list[index]
                          ? checkIn.photo_list[index][0]
                          : `Photo ${index + 1}`;

                      // urls[0] is the full size image
                      const imageUrl = urls[0];
                      return (
                        <View key={index} style={styles.photoItem}>
                          <Image src={imageUrl} style={styles.photoImage} />
                          <Text style={styles.photoLabel}>{photoLabel}</Text>
                        </View>
                      );
                    })}
                  </View>
                )}
              <Text style={styles.checkInTitle}>Check-in Details</Text>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Report Date:</Text>
                <Text style={styles.checkInValue}>
                  {formatDate(checkIn.report_date)}
                </Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Weight:</Text>
                <Text style={styles.checkInValue}>
                  {kgToLbs(checkIn.weight)} lbs
                </Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Body Fat:</Text>
                <Text style={styles.checkInValue}>{checkIn.body_fat}%</Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Lean Body Mass:</Text>
                <Text style={styles.checkInValue}>
                  {kgToLbs(
                    calculateLeanBodyMass(checkIn.weight, checkIn.body_fat)
                  )}{" "}
                  lbs
                </Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Goal:</Text>
                <Text style={styles.checkInValue}>
                  {theme.goal[checkIn.goal]}
                </Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Protein:</Text>
                <Text style={styles.checkInValue}>{checkIn.protein}g</Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Carbs:</Text>
                <Text style={styles.checkInValue}>{checkIn.carbs}g</Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Fat:</Text>
                <Text style={styles.checkInValue}>{checkIn.fat}g</Text>
              </View>
              <View style={styles.checkInDetail}>
                <Text style={styles.checkInLabel}>Calories:</Text>
                <Text style={styles.checkInValue}>
                  {checkIn.protein * 4 + checkIn.carbs * 4 + checkIn.fat * 9}{" "}
                  Cal
                </Text>
              </View>
              {checkIn?.trainer_permission_photos === 0 && (
                <Text style={styles.photoNote}>
                  ⚠️ This client has not allowed you to view their check-in
                  photos.
                </Text>
              )}
            </View>
          );
        } else {
          return (
            <Text style={styles.noResponse}>Loading check-in data...</Text>
          );
        }
      default:
        return null;
    }
  };

  return (
    <Document>
      <Page style={styles.body}>
        <Image style={styles.image} src={mapLogos[theme.name]} />
        <View style={styles.spacer} />
        <Text style={styles.sectionHeader}>SURVEY</Text>
        <Text style={styles.metadata}>Title: {survey_data?.title}</Text>
        <Text style={styles.metadata}>
          Description: {survey_data?.description}
        </Text>
        <Text style={styles.metadata}>
          Check-in Date: {moment.utc(data?.report_date).format("MMMM D, YYYY")}
        </Text>
        <Text style={styles.metadata}>
          Response Date: {moment.utc(data?.created_at).format("MMMM D, YYYY")}
        </Text>
        <Text style={styles.metadata}>Client: {name}</Text>
        <View style={styles.spacer} />
        <Text style={styles.sectionHeader}>RESPONSE</Text>
        {survey_data?.questions?.map((question, index) => {
          const response = response_data.find(
            (r) => r.question_id === question.id
          );
          return (
            <View key={question.id} style={styles.questionContainer}>
              <Text style={styles.questionTitle}>
                {index + 1}. {question.title}
              </Text>
              <Text style={styles.questionDescription}>
                {question.description}
              </Text>
              {response ? (
                renderResponse(question, response)
              ) : (
                <Text style={styles.noResponse}>No response</Text>
              )}
            </View>
          );
        })}
      </Page>
    </Document>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;

  iframe {
    border: none;
    flex: 1;
    height: 100%;
    width: 100%;
  }
`;

export const PreviewSurveyPDF = ({
  open,
  handleClose,
  data = {},
  checkInData = {},
}) => {
  const theme = useContext(ThemeContext);
  const { user } = useContext(AuthContext);
  const currentAccount = user.accounts?.find(
    (account) => account.user_id === data?.user_id
  );

  return (
    <>
      <Modal
        hideAction
        // fullScreen
        // title={`Preview Survey`}
        submitLabel={`Close`}
        open={open}
        onClose={() => handleClose(false)}
        // handleSubmit={handleSend}
      >
        <Container>
          <PDFViewer>
            <SurveyPDF
              data={data}
              name={currentAccount?.name}
              theme={theme}
              checkInData={checkInData}
            />
          </PDFViewer>
        </Container>
      </Modal>
    </>
  );
};
